import { useState, useEffect, useMemo } from "react";

import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import useClassroomsContext from "@/classrooms/useClassroomsContext";
import addHidePropertiesToClassrooms from "@/view/utils/addHidePropertiesToClassrooms";
import getClassroomsContentIsAssignedTo from "@/view/utils/getClassroomsContentIsAssignedTo";
import useGetHiddenContent from "@/view/utils/useGetHiddenContent";
import useHideContent from "@/view/utils/useHideContent";
import useUnhideContent from "@/view/utils/useUnhideContent";
import { popoutContainerCss } from "../../SecondaryActions.style";
import NoClassesMessage from "./NoClassesMessage";
import HideOption from "./HideOption";

const propTypes = {
  contentId: PropTypes.string,
  currentLevelId: PropTypes.string,
  isMobile: PropTypes.bool,
  matchedAssignments: PropTypes.array,
};

const HideOptions = ({
  contentId,
  currentLevelId,
  isMobile,
  matchedAssignments,
}) => {
  const [toastMessage, setToastMessage] = useState("");

  const [createHiddenContent] = useHideContent();
  const [unHideContent] = useUnhideContent();
  const [fetchHiddenContent, { data: hiddenContentData }] = useGetHiddenContent(
    { content_id: contentId },
  );

  const { classrooms } = useClassroomsContext();

  const classroomsWithHideProperties = useMemo(() => {
    return addHidePropertiesToClassrooms(hiddenContentData || [], classrooms);
  }, [JSON.stringify(hiddenContentData), JSON.stringify(classrooms)]);
  const classroomsContentIsAssignedTo = useMemo(
    () => getClassroomsContentIsAssignedTo(matchedAssignments),
    [JSON.stringify(matchedAssignments)],
  );

  useEffect(() => {
    fetchHiddenContent();
  }, []);

  const handleHideContent = async ({ classroomId, name }) => {
    const { data } = await createHiddenContent({ contentId, classroomId });

    if (data) {
      setToastMessage({ msg: `Hidden from ${name}`, error: false });
      fetchHiddenContent();
    } else {
      setToastMessage({ msg: "Article cannot be hidden", error: true });
    }
  };

  const handleUnhideContent = async ({ hideId, name }) => {
    const { error } = await unHideContent(hideId);

    if (!error) {
      setToastMessage({ msg: `Open to ${name}`, error: false });
      fetchHiddenContent();
    } else {
      setToastMessage({ msg: "Article cannot be un-hidden", error: true });
    }
  };

  const sendToast = () => {
    const { msg, error } = toastMessage;
    const statusColor = error ? "danger" : "success";
    const toastProps = {
      children: msg,
      statusColor,
    };

    globalToasterRef.current.displayToast(toastProps);
    setToastMessage("");
  };

  if (!classrooms.length) return <NoClassesMessage isMobile={isMobile} />;

  const renderPopOutOptions = () => {
    return classroomsWithHideProperties.map((classroom) => {
      const disabled = classroomsContentIsAssignedTo?.includes(classroom.id);

      return (
        <HideOption
          classroomId={classroom.id}
          currentLevelId={currentLevelId}
          handleHideContent={handleHideContent}
          handleUnhideContent={handleUnhideContent}
          key={classroom.id}
          name={classroom.name}
          grade={classroom.grade}
          isContentHidden={classroom.isContentHidden}
          hideId={classroom.hideId}
          disabled={disabled}
          dataProps={{ "data-qa-selector": `hide-from-${classroom.id}` }}
        />
      );
    });
  };

  const popoutContents = (
    <>
      {renderPopOutOptions()}
      {toastMessage && sendToast()}
    </>
  );

  if (isMobile) return popoutContents;

  return (
    <ul role="list" css={popoutContainerCss}>
      {popoutContents}
    </ul>
  );
};

HideOptions.propTypes = propTypes;

export default HideOptions;
